"use client"

import { type CSSProperties } from "react"
import cn from "classnames"

import { AnimateText } from "~/components/elements/AnimateText/AnimateText"
import { FadeIn } from "~/components/elements/FadeIn/FadeIn"
import { HtmlRenderer } from "~/components/primitives/HtmlRenderer"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type { CapabilitiesLongInterface } from "./CapabilitiesLong.interface"
import s from "./CapabilitiesLong.module.scss"
import { CapabilitiesLongRow } from "./CapabilitiesLongRow"

export type CapabilitiesLongViewProps = Omit<
  CapabilitiesLongInterface,
  "id"
> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
}

export const CapabilitiesLongView = ({
  showIndex,
  textAlignment,
  eyebrow,
  headline,
  body,
  items,
  config,
  moduleIndex,
  className,
  style,
}: CapabilitiesLongViewProps) => {
  const configProps = getModuleConfigProps(config)

  return (
    <section
      className={cn(
        s.capabilitiesLong,
        `stack-module-${moduleIndex}`,
        className,
      )}
      data-module="capabilities-long"
      data-text-align={textAlignment}
      {...configProps}
      style={{ ...configProps.style, ...style }}
    >
      <div className={cn("container", "default-grid")}>
        <div className={cn(s.inner)}>
          {(headline || body) && (
            <FadeIn className={s.header}>
              {headline && (
                <AnimateText element="h2" className={s.headline}>
                  {headline}
                </AnimateText>
              )}
              {body && (
                <HtmlRenderer
                  animate
                  html={body}
                  element="div"
                  className={s.body}
                />
              )}
            </FadeIn>
          )}
          <div className={s.capabilities}>
            {eyebrow && (
              <FadeIn className={s.eyebrowRow}>
                <AnimateText element="p" className={s.eyebrow}>
                  {eyebrow}
                </AnimateText>
                <span className={s.eyebrowDivider} aria-hidden="true" />
              </FadeIn>
            )}
            <ul className={s.itemList}>
              {items.map((item, index) => (
                <li key={index} className={s.item}>
                  <FadeIn delay={index * 0.08}>
                    <CapabilitiesLongRow
                      item={item}
                      index={index}
                      showIndex={showIndex}
                      textAlignment={textAlignment}
                    />
                  </FadeIn>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}
